import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Box } from '@mui/material'
import type { Group } from './dataPipeline'
import { buildMultiOverlayPoints, type MultiOverlaySeries } from './buildMultiOverlayPoints'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { useStore } from '~/state/useStore'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE } from './chrome/chartStyle'

interface Props {
  series: MultiOverlaySeries[]
  groups: Group[]
}

/** N picked timeframes overlaid as N index-aligned lines on one shared Y-axis (same metric, different
 *  windows). X position is the relative bucket index (#1, #2, …); absolute dates differ per series so
 *  they are intentionally not on the axis. Generalizes ComparisonOverlayChart's period mode 2→N.
 *  Colors are POSITIONAL (store chipPalette by series index) — a timeframe series is not a dim
 *  identity, so there is no value to key a stable color off of; slot N always means "the Nth
 *  picked timeframe", not a specific period. */
export function MultiTimeframeOverlayChart({ series, groups }: Props) {
  const palette = useStore((s) => s.personal.chipPalette)
  const points = buildMultiOverlayPoints(series, groups)
  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <LineChart data={points} margin={{ top: 16, left: 24, right: 24, bottom: 24 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey="pos" tickFormatter={(p) => `#${Number(p) + 1}`} tick={AXIS_TICK} />
          <YAxis tickFormatter={formatAxisTick} tick={AXIS_TICK} />
          {/* v1: position-based tooltip (#N). Per-series absolute dates are a deferred enhancement (spec Part B). */}
          <Tooltip
            labelFormatter={(p) => `Periode #${Number(p) + 1}`}
            formatter={(value) =>
              typeof value === 'number' ? formatCompactNumber(value) : String(value)
            }
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          <Legend />
          {series.map((s, i) => (
            <Line
              key={s.id}
              dataKey={s.id}
              name={s.label}
              type="monotone"
              stroke={palette[i % palette.length] ?? '#000000'}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
